// 📦 Import des hooks React et d’Axios
import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { logDev } from "../utils/logger.js";

// 🔹 Création d’un contexte React pour partager les infos de groupe
const GroupeContext = createContext();

// 🔹 Provider qui englobe l’application et fournit les infos du groupe
export const GroupeProvider = ({ children }) => {
  // ✅ Initialisation : on essaie d’abord de lire dans sessionStorage,
  // sinon on prend localStorage (utile si l’utilisateur a rechargé la page)
  const initialGroupeId = sessionStorage.getItem("groupeId") || localStorage.getItem("groupeId");
  const initialRole = sessionStorage.getItem("role") || localStorage.getItem("role");

  logDev("[GroupeContext] initialisation", { initialGroupeId, initialRole });
  
  // ✅ États internes du contexte
  const [groupeId, setGroupeId] = useState(initialGroupeId); // identifiant du groupe actif
  const [role, setRole] = useState(initialRole);             // rôle de l’utilisateur (admin/user)
  const [isReady, setIsReady] = useState(false);

  const reloadGroupeActif = async () => {
  if (!groupeId) return;
  try {
    const res = await axios.get(`${process.env.REACT_APP_API}/groupes/${groupeId}`);
    setGroupeActif(res.data);
    logDev("[GroupeContext] groupe rechargé", res.data);
  } catch (err) {
    logDev("❌ Erreur rechargement groupe actif", { message: err.message });
  }
};

  // 🔄 Effet : relecture immédiate du storage au montage
  useEffect(() => {
  const storedId = sessionStorage.getItem("groupeId") || localStorage.getItem("groupeId");
  const storedRole = sessionStorage.getItem("role") || localStorage.getItem("role");

  if (storedId && !groupeId) setGroupeId(storedId);
  if (storedRole && (role === "loading" || !role)) setRole(storedRole);
}
, []);

  // 🔧 Setter centralisé pour mettre à jour le groupe actif
  // - met à jour l’état React
  // - persiste dans sessionStorage ET localStorage
  const setGroupe = (id, roleValue) => {
    logDev("[GroupeContext] setGroupe appelé", { id, roleValue });
    if (id) {
      sessionStorage.setItem("groupeId", id);
      localStorage.setItem("groupeId", id);
      setGroupeId(id);
    }
    if (roleValue) {
      sessionStorage.setItem("role", roleValue);
      localStorage.setItem("role", roleValue);
      setRole(roleValue);
    }
  };

  useEffect(() => {
    if (!groupeId || !/^[0-9a-fA-F]{24}$/.test(groupeId)) {
      logDev("[GroupeContext] aucun groupe valide", { groupeId });
      setIsReady(true);
    return;
    }

  reloadGroupeActif().finally(() => setIsReady(true)); // ✅ recharge tout le groupe (nom, emoji, couleur)
  }, [groupeId]);

  const [groupeActif, setGroupeActif] = useState(null);

  // 🔹 Fournit les valeurs du contexte à tous les composants enfants
  return (
    <GroupeContext.Provider
    value={{
      groupeId,
      role,
      setGroupe,
      groupeActif,
      setGroupeActif,
      isReady,
      reloadGroupeActif,
      nom: groupeActif?.nom || "",  
      emoji: groupeActif?.emoji || "",
      couleur: groupeActif?.couleur || "#71685e",
      nomGroupeComplet: `${groupeActif?.emoji || ""} ${groupeActif?.nom || ""}`
    }}
  >

      {children}
    </GroupeContext.Provider>
  );
};

// 🔹 Hook utilitaire pour consommer facilement le contexte
export const useGroupe = () => useContext(GroupeContext);
